import * as L from 'leaflet';
import { createPinDivIcon, PIN_BODY_PATH } from '../../../shared/utils/pin-shape';
import { FleetStatus } from '../models/monitoring.model';

/**
 * Fleet-status pin + cluster icons for the Map tab, built on the app's
 * shared teardrop shape (`shared/utils/pin-shape.ts`) — see
 * README-2a-map-tab.md's "Markers" section for the status glyphs/colours
 * this reproduces pixel-for-pixel.
 */

/**
 * Pin/cluster fill per status. Warning deliberately does NOT use the
 * status "Mark" colour (dark olive, `rgb(130,121,0)`) used for text/dots
 * elsewhere on this page — the design handoff flagged that olive as
 * reading muddy at pin/cluster size behind a white glyph or count, and
 * recommended swapping to the yellow/500 design-system step instead
 * (confirmed — see PR discussion). CSS custom properties work here because
 * this SVG is inserted into the real DOM via a Leaflet `divIcon`, not
 * serialized as a static string asset.
 */
const PIN_FILL: Record<FleetStatus, string> = {
  ok: '#006800',
  warning: 'var(--colorscales-yellow-500)',
  error: '#990000',
  offline: '#999999',
  shutdown: '#999999',
};

/** Cluster fill when the worst member status is offline/shutdown is darker
 *  (#4D4D4D) than the individual pin's #999 — legibility for the white
 *  count text specifically; every other status is the same as `PIN_FILL`. */
const CLUSTER_FILL: Record<FleetStatus, string> = {
  ...PIN_FILL,
  offline: '#4D4D4D',
  shutdown: '#4D4D4D',
};

/** Worst-status precedence — also the row order for the cluster hover
 *  tooltip (deliberately NOT the legend's ok-first order). */
export const STATUS_SEVERITY_ORDER: FleetStatus[] = ['error', 'warning', 'offline', 'shutdown', 'ok'];

/** The worse of a group of statuses, per the precedence above. */
export function worstStatus(statuses: FleetStatus[]): FleetStatus {
  for (const status of STATUS_SEVERITY_ORDER) {
    if (statuses.includes(status)) return status;
  }
  return 'ok';
}

/** White, 2.2px round-capped glyph centred in the pin's head. Offline and
 *  shutdown share the dash — distinguished in the tooltip/rail, not the pin. */
function glyphMarkup(status: FleetStatus): string {
  switch (status) {
    case 'ok':
      return '<path d="M8.6 12.2l2.5 2.5 4.6-5.2" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>';
    case 'warning':
      return '<path d="M12 7.4v5.1" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/><circle cx="12" cy="16" r="1.3" fill="#fff"/>';
    case 'error':
      return '<path d="M9.2 9.2l5.6 5.6M14.8 9.2l-5.6 5.6" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/>';
    case 'offline':
    case 'shutdown':
      return '<path d="M8.6 12h6.8" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/>';
  }
}

/** The pin's own box size — also the basis for the halo's size (see
 *  `createHaloIcon`), which must scale this UNIFORMLY (same factor on both
 *  axes) to stay concentric with it. A halo sized independently of these
 *  drifted out of alignment: two different width/height ratios stretch the
 *  same path into two subtly different silhouettes, so the ring framed the
 *  pin unevenly (thicker on one side) instead of tracing its actual edge. */
const PIN_WIDTH = 30;
const PIN_HEIGHT = 40;

/**
 * An individual device pin — 30×40, status fill + glyph. Always the plain
 * (unselected) form: "this pin is selected" is shown via a separate halo
 * overlay (`createHaloIcon`, `MonitoringComponent.haloMarker`), not by
 * resizing/re-ringing this icon — see that field's doc comment for why.
 */
export function createPinIcon(status: FleetStatus): L.DivIcon {
  return createPinDivIcon({
    width: PIN_WIDTH,
    height: PIN_HEIGHT,
    fill: PIN_FILL[status],
    inner: glyphMarkup(status),
    className: 'pc-map-pin',
  });
}

/**
 * The selection halo — a larger, ring-ONLY outline (no fill, no white
 * keyline, no glyph) at the same pin coordinates, drawn behind it. Built
 * directly from `PIN_BODY_PATH` rather than `createPinDivIcon` since that
 * helper always draws the body's white keyline stroke too, which isn't
 * wanted here. A separate overlay, not a variant of the pin's own icon —
 * see `MonitoringComponent.haloMarker`.
 */
export function createHaloIcon(): L.DivIcon {
  // SAME box, anchor, AND path as the pin — no independent size and no
  // scale transform. Two earlier attempts tried to draw a slightly BIGGER
  // outline (own box, then a scaled path in a shared box) and neither sat
  // concentric with the pin — evidently even "the same scale factor on
  // both axes" doesn't guarantee two independently-computed shapes land
  // pixel-identical, whether due to viewBox rounding or the transform's
  // own centre-point math being slightly off. Using the IDENTICAL path
  // sidesteps the question entirely: there is no second shape to
  // (mis)align, only a thick stroke drawn on the pin's own outline,
  // rendered behind it (zIndexOffset below the pin — see `haloMarker`).
  // The stroke straddles that shared edge; its outer half is what actually
  // reads as the ring, since the inner half is covered by the opaque pin.
  // overflow:visible — the stroke extends past the 24×33 viewBox on all
  // sides; SVGs clip to their viewBox by default, which would otherwise
  // cut it off right at the edge instead of showing the outer half.
  const svg = `
    <svg width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="0 0 24 33" style="overflow: visible" xmlns="http://www.w3.org/2000/svg">
      <path d="${PIN_BODY_PATH}" fill="none" stroke="#007FE9" stroke-width="4"/>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'pc-map-pin-halo',
    iconSize: [PIN_WIDTH, PIN_HEIGHT],
    iconAnchor: [PIN_WIDTH / 2, PIN_HEIGHT],
  });
}

/**
 * A cluster pin — same silhouette at 36×46, filled with the worst member
 * status, carrying the member count in place of a glyph.
 */
export function createClusterIcon(count: number, status: FleetStatus): L.DivIcon {
  const text = `
    <text
      x="12" y="15"
      text-anchor="middle" dominant-baseline="middle"
      font-family="'Open Sans', sans-serif" font-size="12" font-weight="700"
      fill="#fff"
    >${count}</text>`;
  return createPinDivIcon({
    width: 36,
    height: 46,
    fill: CLUSTER_FILL[status],
    inner: text,
    className: 'pc-cluster-pin',
  });
}
